import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearch } from '../contexts/SearchContext';
import { entityPath } from '../utils/entityRoutes';
import { globalSearchPlaceholder, pageSearchPlaceholder } from '../utils/globalSearchPlaceholder';
import Icon from './Icon';
import '../styles/components/GlobalSearch.css';

const ACCOUNT_RESULT_TYPES: ReadonlySet<string> = new Set(['client', 'project', 'clientInvoice', 'clientQuote', 'accountVendor', 'person']);

/** The word shown beside a result; the account types share names with project ones, so they are spelled out here. */
const TYPE_LABELS: Record<string, string> = { clientInvoice: 'invoice', clientQuote: 'quote', accountVendor: 'vendor' };

const GlobalSearch: React.FC = () => {
  const { query, setQuery, results, isSearching, clearSearch, pageFilter, setPageFilter, accountScope, accountStatus, openAccountResult } = useSearch();
  const accountLoading = accountScope && accountStatus.loading;
  const accountFailed = accountScope && accountStatus.failed;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  // Below 640px the input is collapsed to a single icon button (there's no
  // room for an always-open search bar next to the page title) — tapping it
  // expands the bar as an overlay instead of squeezing the rest of the header.
  const [mobileExpanded, setMobileExpanded] = useState(false);
  // The full descriptive placeholder overflows narrow mobile widths and
  // pushes the clear (×) button out of the visible search bar entirely.
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 480);
  // A list page that the box narrows says so (short enough for a phone too).
  const pagePlaceholder = pageSearchPlaceholder(pathname);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
        setMobileExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const expandMobileSearch = () => {
    setMobileExpanded(true);
    // Wait for the overlay to mount before focusing it.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleResultClick = useCallback((result: any) => {
    // Workspace-level results open through the provider: a project, or a vendor
    // in another project, needs a workspace switch first.
    if (ACCOUNT_RESULT_TYPES.has(result.type)) {
      void openAccountResult(result);
      setIsOpen(false);
      setSelectedIndex(-1);
      clearSearch();
      return;
    }
    let path = '/';
    switch (result.type) {
      case 'vendor':
        path = entityPath('vendor', result.data?.id) ?? '/vendors';
        break;
      case 'budget':
        path = '/budget';
        break;
      case 'menu':
        path = '/menu';
        break;
      case 'task':
        path = entityPath('task', result.data?.id) ?? '/activities';
        break;
      case 'favour':
        path = '/procurement/inventory';
        break;
      case 'song':
        path = '/music';
        break;
      case 'guideline':
        path = '/guidelines';
        break;
      case 'decor':
        path = '/decor';
        break;
      case 'stationery':
        path = entityPath('stationeryItem', result.data?.id) ?? '/stationery';
        break;
      case 'quote':
        path = entityPath('quote', result.data?.id) ?? '/quotes';
        break;
      case 'payment':
        // PaymentsView's record editor needs router state we don't have here,
        // so land on the payments list rather than a half-loaded form.
        path = '/payments';
        break;
      case 'contributor':
        path = '/payments/contributors';
        break;
      case 'bundle':
        path = result.data?.id ? `/bundle/${result.data.id}` : '/asoebi/bundles';
        break;
      case 'product':
        path = entityPath('catalogueItem', result.data?.id) ?? '/catalogue';
        break;
      case 'venue':
        path = entityPath('venue', result.data?.id) ?? '/venue';
        break;
      case 'floorplan':
        // Floor plans are edited inside their venue.
        path = entityPath('venue', result.data?.venue_id) ?? '/venue';
        break;
      case 'inventory':
        path = '/procurement/inventory';
        break;
      case 'vehicle':
        path = entityPath('vehicle', result.data?.id) ?? '/logistics';
        break;
      case 'driver':
        path = entityPath('driver', result.data?.id) ?? '/logistics';
        break;
      case 'movement':
        path = '/logistics';
        break;
    }

    navigate(path);
    setIsOpen(false);
    setSelectedIndex(-1);
    clearSearch();
  }, [clearSearch, navigate, openAccountResult]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            handleResultClick(results[selectedIndex]);
          } else if (query.trim()) {
            // With nothing highlighted, Enter used to swallow the keypress and
            // do nothing at all. The dropdown already covers "take me to this
            // record"; Enter is the other thing you'd want from a search box —
            // narrow the page in front of you.
            setPageFilter(query.trim());
            setIsOpen(false);
            setSelectedIndex(-1);
            setMobileExpanded(false);
            inputRef.current?.blur();
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          setMobileExpanded(false);
          inputRef.current?.blur();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, results, handleResultClick, query, setPageFilter]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    // Typing also narrows the list on the page being looked at (Enter still applies it too).
    setPageFilter(value.trim());
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (query) {
      setIsOpen(true);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vendor': return 'wallet';
      case 'budget': return 'star';
      case 'menu': return 'utensils';
      case 'task': return 'check';
      case 'favour': return 'gift';
      case 'song': return 'music';
      case 'guideline': return 'list';
      case 'decor': return 'sparkles';
      case 'stationery': return 'document';
      case 'quote': return 'document';
      case 'payment': return 'creditCard';
      case 'contributor': return 'users';
      case 'bundle': return 'gift';
      case 'product': return 'grid';
      case 'venue': return 'map';
      case 'floorplan': return 'map';
      case 'inventory': return 'briefcase';
      case 'vehicle': return 'mapPin';
      case 'driver': return 'user';
      case 'movement': return 'mapPin';
      case 'client': return 'users';
      case 'project': return 'folder';
      case 'clientInvoice': return 'creditCard';
      case 'clientQuote': return 'document';
      case 'accountVendor': return 'briefcase';
      case 'person': return 'user';
      default: return 'check';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'vendor': return '#8b5a3c';
      case 'budget': return '#d4a574';
      case 'menu': return '#a09080';
      case 'task': return '#7a9c5b';
      case 'favour': return '#c05b8a';
      case 'song': return '#9c5b7a';
      case 'guideline': return '#5b9c8a';
      case 'decor': return '#c19660';
      case 'stationery': return '#7a6b8a';
      case 'quote': return '#8a6b5b';
      case 'payment': return '#4a7a5b';
      case 'contributor': return '#7a5b9c';
      case 'bundle': return '#b07a9c';
      case 'product': return '#5b7a8a';
      case 'venue': return '#6b8a5b';
      case 'floorplan': return '#8a9c5b';
      case 'inventory': return '#9c8a5b';
      case 'vehicle': return '#5b6b9c';
      case 'driver': return '#7a8ab0';
      case 'movement': return '#8a5b6b';
      case 'client': case 'project': case 'clientInvoice': case 'clientQuote': case 'accountVendor': case 'person':
        return 'var(--primary)';
      default: return '#6b5b4b';
    }
  };

  return (
    <div className={`global-search${mobileExpanded ? ' gs-mobile-expanded' : ''}`} ref={searchRef}>
      <button
        type="button"
        className="gs-mobile-trigger"
        onClick={expandMobileSearch}
        title="Search"
        aria-label="Search"
      >
        <Icon name="search" size={18} />
      </button>
      <input
        ref={inputRef}
        type="text"
        className="search-input"
        placeholder={pagePlaceholder ?? (isNarrow ? 'Search for all records' : globalSearchPlaceholder(pathname, accountScope))}
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
      />
      <Icon name="search" size={16} className="search-icon" />
      {query && (
        <button
          className="search-clear-btn"
          onClick={clearSearch}
          aria-label="Clear search"
        >
          <Icon name="close" size={14} />
        </button>
      )}
      {pageFilter && (
        <div className="gs-page-filter" role="status">
          <span>Filtering this page by &ldquo;{pageFilter}&rdquo;</span>
          <button
            type="button"
            onClick={() => setPageFilter('')}
            aria-label="Clear page filter"
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      )}
      {mobileExpanded && !query && (
        <button
          type="button"
          className="gs-mobile-close"
          onClick={() => { setMobileExpanded(false); setIsOpen(false); }}
          aria-label="Close search"
        >
          <Icon name="close" size={14} />
        </button>
      )}

      {isOpen && (
        <div className="search-results">
          {isSearching || (accountLoading && results.length === 0) ? (
            <div className="search-loading">
              <div className="gs-loading-spinner"></div>
              <span>{accountLoading ? 'Loading results...' : 'Searching...'}</span>
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="search-results-header">
                <span className="results-count">{results.length} results</span>
                <kbd className="keyboard-shortcut">ESC</kbd>
              </div>
              <div className="results-list">
                {results.map((result, index) => (
                  <div
                    key={result.id}
                    className={`search-result-item ${selectedIndex === index ? 'selected' : ''}`}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="result-icon" style={ { color: getTypeColor(result.type) }}>
                      <Icon name={getTypeIcon(result.type)} size={16} />
                    </div>
                    <div className="result-content">
                      <div className="result-title">{typeof result.title === 'string' ? result.title : String(result.title ?? '')}</div>
                      <div className="result-description">{typeof result.description === 'string' ? result.description : String(result.description ?? '')}</div>
                      <div className="result-category">{typeof result.category === 'string' ? result.category : String((result.category as any)?.name ?? result.category ?? '')}</div>
                    </div>
                    <div className="result-type">{TYPE_LABELS[result.type] ?? result.type}</div>
                  </div>
                ))}
              </div>
            </>
          ) : query ? (
            <div className="search-no-results">
              <Icon name="search" size={24} />
              <span>No results found for "{query}"</span>
              <span className="search-tips">
                {accountScope
                  ? 'Try a client, project, invoice or quote number, or a vendor'
                  : 'Try searching for vendors, budget categories, events, menu items, or asoebi'}
              </span>
            </div>
          ) : null}
          {accountLoading && results.length > 0 && (
            <div className="search-loading"><span>Loading more results...</span></div>
          )}
          {accountFailed && <div className="search-tips" role="status">Some results could not load</div>}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
